import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ChevronRight, MessageCircle } from "lucide-react";
import noise from "../../assets/beauty/noise.png";

const green = "#43BD98";
const accent = "#D9B8CE";

export default function FinalCTA2() {
	const { t } = useTranslation();

	return (
		<section
			className="relative overflow-hidden py-24"
			style={{
				background:
					"linear-gradient(180deg, #fff9fc 0%, #f7ebe8 45%, #fff9fc 100%)",
				fontFamily: "'Poppins', sans-serif",
			}}
		>
			{/* Soft decorative blobs */}
			<div
				className="absolute w-[420px] h-[420px] rounded-full blur-[140px] opacity-40 top-[-120px] left-[-100px]"
				style={{ backgroundColor: accent }}
				aria-hidden="true"
			/>
			<div
				className="absolute w-[360px] h-[360px] rounded-full blur-[120px] opacity-30 bottom-[-100px] right-[-80px]"
				style={{ backgroundColor: "#E7F8F2" }}
				aria-hidden="true"
			/>

			{/* Noise texture */}
			<div
				className="absolute inset-0 opacity-[0.45] pointer-events-none"
				style={{
					backgroundImage: `url(${noise})`,
					backgroundRepeat: "repeat",
					backgroundSize: "250px 250px",
				}}
			/>

			<div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
				{/* Title */}
				<motion.h2
					initial={{ opacity: 0, y: 30 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.7, ease: "easeOut" }}
					viewport={{ once: true }}
					className="font-extrabold mb-5"
					style={{
						fontSize: "clamp(1.8rem, 5vw, 2.8rem)",
						lineHeight: 1.15,
						color: "#1a1a1a",
					}}
				>
					{t("salon2.finalCta.title")}
				</motion.h2>

				{/* Subtitle */}
				<motion.p
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.7, delay: 0.1 }}
					viewport={{ once: true }}
					className="mb-10 mx-auto max-w-xl"
					style={{
						color: "#555",
						fontSize: "1.1rem",
						lineHeight: 1.7,
					}}
				>
					{t("salon2.finalCta.subtitle")}
				</motion.p>

				{/* CTAs */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.7, delay: 0.2 }}
					viewport={{ once: true }}
					className="flex flex-col sm:flex-row justify-center gap-4 mb-8"
				>
					<a
						href="https://creator.applaby.com?onboarding=true&appType=Services&appSubtype=Salon"
						target="_blank"
						rel="noreferrer"
						className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-semibold text-white shadow-lg hover:shadow-xl hover:scale-[1.03] transition-all duration-300"
						style={{
							background: `linear-gradient(135deg, ${green} 0%, #33a884 100%)`,
							boxShadow: "0 8px 30px rgba(67,189,152,0.25)",
						}}
					>
						{t("salon2.finalCta.cta")} <ChevronRight size={18} />
					</a>

					<a
						href="https://wa.me/34608916357"
						target="_blank"
						rel="noreferrer"
						className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-semibold transition-all duration-300 hover:bg-white"
						style={{
							color: "#333",
							border: `1px solid ${accent}80`,
							backgroundColor: "rgba(255,255,255,0.7)",
						}}
					>
						<MessageCircle size={18} style={{ color: "#25D366" }} />
						{t("salon2.finalCta.secondary")}
					</a>
				</motion.div>

				{/* Trust line */}
				<motion.p
					initial={{ opacity: 0 }}
					whileInView={{ opacity: 1 }}
					transition={{ duration: 0.6, delay: 0.35 }}
					viewport={{ once: true }}
					className="text-sm"
					style={{ color: "#888" }}
				>
					{t("salon2.finalCta.trust")}
				</motion.p>
			</div>
		</section>
	);
}
